import { useState } from "react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSEO } from "@/lib/useSEO";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  useSEO({
    title: "Reset your password — Hearth & Crate",
    description: "Forgot your Hearth & Crate password? Enter your email and we'll send you a reset link.",
    canonical: "https://hearthpro.co.uk/forgot-password",
  });

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await api.post("/auth/request-password-reset", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#f7f5f0]" data-testid="forgot-password-page">
      <div className="flex flex-col px-8 sm:px-16 py-10 lg:py-20">
        <div className="font-display text-xl font-medium tracking-tight">
          Hearth<span className="text-[#c0612f]">.</span>
        </div>
        <div className="flex-1 flex items-center">
          <div className="w-full max-w-sm animate-fade-up">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#8a8476] mb-4">Reset password</div>
            <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-none">
              Locked out?<br />
              <span className="text-[#4e7b5b]">No drama.</span>
            </h1>
            <p className="mt-4 text-sm text-[#57534e] leading-relaxed">
              Enter the email you signed up with and we'll send a one-time reset link. Link is valid for 1 hour.
            </p>

            {sent ? (
              <div data-testid="forgot-sent-message" className="mt-8 bg-[#e8f0e8] border border-[#c5dcc5] text-[#1b3b36] rounded-[3px] px-4 py-3 text-sm">
                If an account exists for <b>{email}</b>, we've sent a reset link to it. Check your inbox.
              </div>
            ) : (
              <form className="space-y-5 mt-8" onSubmit={onSubmit} data-testid="forgot-form">
                <div>
                  <Label className="text-[11px] uppercase tracking-[0.22em] text-[#8a8476]">Email</Label>
                  <Input
                    type="email"
                    required
                    data-testid="forgot-email-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-2 h-11 bg-white border-[#e5e2d9] rounded-[4px]"
                  />
                </div>
                {error && (
                  <div data-testid="forgot-error" className="text-sm text-[#d94f3d] bg-[#fbeae8] px-3 py-2 rounded-[3px]">{error}</div>
                )}
                <Button type="submit" disabled={busy} data-testid="forgot-submit-button" className="w-full h-11 bg-[#1b3b36] hover:bg-[#2c5a53] text-white rounded-[4px] font-medium tracking-wide">
                  {busy ? "Sending…" : "Send reset link"}
                </Button>
              </form>
            )}

            <div className="text-sm text-[#57534e] mt-6">
              Remembered it? <Link to="/login" data-testid="forgot-back-to-login" className="text-[#1b3b36] underline underline-offset-2">Sign in</Link>
            </div>
          </div>
        </div>
        <div className="text-xs text-[#8a8476]">© {new Date().getFullYear()} Hearth Operations</div>
      </div>
      <div className="relative hidden lg:block overflow-hidden grain">
        <img
          src="https://images.pexels.com/photos/4253133/pexels-photo-4253133.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#1b3b36]/70 via-[#1b3b36]/30 to-transparent" />
      </div>
    </div>
  );
}
